import React, { useState, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../../../store';
import { selectStructuralFramingSizes, selectStructuralFramingTypes, selectStructuralRafterSpacings, selectMountSystems } from '../../../store/selectors/databaseSelectors';
import { addStructuralFramingSize, addStructuralFramingType, addStructuralRafterSpacing, addStructuralMountSystem } from '../../../store/slices/databaseSlice';
import { structuralOptionsService } from '../../../services';
import { Structural, StructuralCategory } from '../../../types';
import { TOAST_CONFIG, TOAST_WARNING_CONFIG } from '../../../constants';
import toast from 'react-hot-toast';

interface StructuralForm {
  category: StructuralCategory;
  value: string;
}

type StructuralTab = 'framing_size' | 'framing_type' | 'rafter_spacing' | 'mount_system';

const STRUCTURAL_TABS: { id: StructuralTab; label: string; category: StructuralCategory }[] = [
  { id: 'framing_size', label: 'Framing Sizes', category: StructuralCategory.FRAMING_SIZE },
  { id: 'framing_type', label: 'Framing Types', category: StructuralCategory.FRAMING_TYPE },
  { id: 'rafter_spacing', label: 'Rafter Spacings', category: StructuralCategory.RAFTER_SPACING },
  { id: 'mount_system', label: 'Mount Systems', category: StructuralCategory.MOUNT_SYSTEM }
];

export const StructuralManagement: React.FC = () => {
  const dispatch = useAppDispatch();
  
  // Get data from Redux
  const framingSizes = useAppSelector(selectStructuralFramingSizes);
  const framingTypes = useAppSelector(selectStructuralFramingTypes);
  const rafterSpacings = useAppSelector(selectStructuralRafterSpacings);
  const mountSystems = useAppSelector(selectMountSystems);
  
  // Form states
  const [structuralForm, setStructuralForm] = useState<StructuralForm>({
    category: StructuralCategory.FRAMING_SIZE,
    value: ''
  });
  
  // UI states
  const [activeTab, setActiveTab] = useState<StructuralTab>('framing_size');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localStructuralData, setLocalStructuralData] = useState({
    framing_sizes: framingSizes,
    framing_types: framingTypes,
    rafter_spacings: rafterSpacings,
    mount_systems: mountSystems
  });

  // Initialize local state with Redux data
  useEffect(() => {
    setLocalStructuralData({
      framing_sizes: framingSizes,
      framing_types: framingTypes,
      rafter_spacings: rafterSpacings,
      mount_systems: mountSystems
    });
  }, [framingSizes, framingTypes, rafterSpacings, mountSystems]);

  // Update form category when tab changes
  useEffect(() => {
    const currentTab = STRUCTURAL_TABS.find(tab => tab.id === activeTab);
    if (currentTab) {
      setStructuralForm(prev => ({ ...prev, category: currentTab.category }));
    }
  }, [activeTab]);

  // Get current data for active tab
  const getCurrentData = (): Structural[] => {
    switch (activeTab) {
      case 'framing_size': return localStructuralData.framing_sizes;
      case 'framing_type': return localStructuralData.framing_types;
      case 'rafter_spacing': return localStructuralData.rafter_spacings;
      case 'mount_system': return localStructuralData.mount_systems;
      default: return [];
    }
  };

  // Get dispatch function for active tab
  const getDispatchFunction = () => {
    switch (activeTab) {
      case 'framing_size': return addStructuralFramingSize;
      case 'framing_type': return addStructuralFramingType;
      case 'rafter_spacing': return addStructuralRafterSpacing;
      case 'mount_system': return addStructuralMountSystem;
      default: return addStructuralFramingSize;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!structuralForm.value.trim()) {
      toast('Please enter a value', TOAST_WARNING_CONFIG);
      return;
    }

    setIsSubmitting(true);
    try {
      const currentData = getCurrentData();
      const displayOrder = currentData.length + 1;
      
      const newStructural = await structuralOptionsService.createStructural({
        category: structuralForm.category,
        value: structuralForm.value.trim(),
        displayOrder: displayOrder
      });
      
      // Update Redux state
      const dispatchFunction = getDispatchFunction();
      dispatch(dispatchFunction(newStructural));
      
      // Update local state
      setLocalStructuralData(prev => {
        const updated = { ...prev };
        switch (activeTab) {
          case 'framing_size': updated.framing_sizes = [...prev.framing_sizes, newStructural]; break;
          case 'framing_type': updated.framing_types = [...prev.framing_types, newStructural]; break;
          case 'rafter_spacing': updated.rafter_spacings = [...prev.rafter_spacings, newStructural]; break;
          case 'mount_system': updated.mount_systems = [...prev.mount_systems, newStructural]; break;
        }
        return updated;
      });
      
      // Reset form
      setStructuralForm(prev => ({ ...prev, value: '' }));
      
      toast.success(`Structural ${STRUCTURAL_TABS.find(t => t.id === activeTab)?.label?.slice(0, -1)} created successfully!`, TOAST_CONFIG.toastOptions.success);
    } catch (error) {
      console.error('Error creating structural item:', error);
      toast.error('Failed to create structural item. Please try again.', TOAST_CONFIG.toastOptions.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentData = getCurrentData();

  const getPlaceholder = () => {
    switch (activeTab) {
      case 'framing_size': return 'e.g., 2x4, 2x6, 2x8';
      case 'framing_type': return 'e.g., Wood, Steel, Aluminum';
      case 'rafter_spacing': return 'e.g., 16" O.C., 24" O.C.';
      case 'mount_system': return 'e.g., ROOF, GROUND, ROOF&GROUND';
      default: return 'Enter value';
    }
  };

  return (
    <div className="p-4">
      {/* Tabs */}
      <div className="flex flex-wrap border-b border-slate-200 mb-4">
        {STRUCTURAL_TABS.map((tab) => {
          // Get correct count for each tab
          let tabCount = 0;
          switch (tab.id) {
            case 'framing_size': tabCount = localStructuralData.framing_sizes.length; break;
            case 'framing_type': tabCount = localStructuralData.framing_types.length; break;
            case 'rafter_spacing': tabCount = localStructuralData.rafter_spacings.length; break;
            case 'mount_system': tabCount = localStructuralData.mount_systems.length; break;
          }
          
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-3 py-2 font-medium text-sm border-b-2 transition-colors duration-200 mr-4 mb-2 ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              {tab.label} ({tabCount})
            </button>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Form Section */}
        <div className="bg-white rounded-lg p-4 border border-slate-200 shadow-sm">
          <h3 className="text-base font-semibold text-slate-900 mb-3">
            Add New {STRUCTURAL_TABS.find(t => t.id === activeTab)?.label?.slice(0, -1)}
          </h3>

          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                {activeTab === 'framing_size' ? 'Size *' : 
                 activeTab === 'framing_type' ? 'Type *' : 
                 activeTab === 'rafter_spacing' ? 'Spacing *' :
                 activeTab === 'mount_system' ? 'System *' : 'Value *'}
              </label>
              <input
                type="text"
                value={structuralForm.value}
                onChange={(e) => setStructuralForm(prev => ({ ...prev, value: e.target.value }))}
                placeholder={getPlaceholder()}
                className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            
            <button
              type="submit"
              disabled={isSubmitting || !structuralForm.value.trim()}
              className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {isSubmitting ? 'Creating...' : `Create ${STRUCTURAL_TABS.find(t => t.id === activeTab)?.label?.slice(0, -1)}`}
            </button>
          </form>
        </div>

        {/* List Section */}
        <div className="bg-white rounded-lg p-4 border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold text-slate-900">
              Existing {STRUCTURAL_TABS.find(t => t.id === activeTab)?.label}
            </h3>
            <span className="text-xs text-slate-500">
              {currentData.length} items
            </span>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {currentData.length > 0 ? (
              <div className="flex flex-wrap gap-2 p-2">
                {currentData.map((item) => (
                  <div key={item.id} className="inline-flex items-center px-3 py-1 bg-slate-50 text-slate-800 rounded-full border border-slate-200 hover:bg-slate-100 transition-colors">
                    <span className="text-xs font-medium">{item.value}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-xs text-center py-8">
                No {STRUCTURAL_TABS.find(t => t.id === activeTab)?.label?.toLowerCase()} found
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};